import { takeDroppedFiles, takeDroppedFolder } from './pending-drop';
import { trackProduct } from './product-tracker';
import { accept, isSupported } from './upload';

/** What the operator chose: the readable files, the folder's name, and the names Paige will not read. */
export interface PickedFiles {
  files: File[];
  folderName: string;
  skipped: string[];
}

/** The browser's file picker, or the files just dropped; an empty list is the person closing it. */
export function pickFiles(): Promise<File[]> {
  const dropped = takeDroppedFiles();
  if (dropped) return Promise.resolve(dropped);
  return pickThrough((input) => {
    input.multiple = true;
    input.accept = accept;
  });
}

/**
 * A folder and its subfolders, in the browser, through `<input webkitdirectory>`, which every
 * browser has. The File System Access API's directory picker was tried first until 2026-09-30: after
 * the folder dialog it asks a second question ("Let this site view files?"), and where that prompt
 * is refused, missed or not shown at all (some embedded browsers) the picker rejected, the page took
 * it for the person closing the dialog, and "Add pages" looked like it opened the dialog again.
 * Every readable file goes in; the rest are named as skipped so the progress line can say so. An
 * empty result with no folder name is the person closing the picker. A folder dragged onto the
 * page (PageDropZone.tsx) answers instead of the dialog, with no browser prompt.
 */
export async function pickFolder(): Promise<PickedFiles> {
  const dropped = takeDroppedFolder();
  if (dropped) return dropped;
  const files = await pickThrough((input) => {
    input.setAttribute('webkitdirectory', '');
  });
  const folderName = files[0]?.webkitRelativePath.split('/')[0] ?? '';
  return sortOut(files, folderName, (file) => file.webkitRelativePath || file.name);
}

/** pages.folder_chosen with the counts; a closed picker (nothing chosen) is not a choice. */
export function trackFolder(picked: PickedFiles): PickedFiles {
  if (picked.files.length > 0 || picked.skipped.length > 0)
    trackProduct('pages.folder_chosen', {
      count: picked.files.length,
      skipped: picked.skipped.length,
    });
  return picked;
}

/** Readable files first, in path order, so the sample and the waves read as the folder does. */
export function sortOut(
  files: File[],
  folderName: string,
  pathOf: (file: File) => string,
): PickedFiles {
  const readable = files.filter((file) => isSupported(file.name));
  readable.sort((a, b) => pathOf(a).localeCompare(pathOf(b)));
  const skipped = files.filter((file) => !isSupported(file.name)).map(pathOf);
  return { files: readable, folderName, skipped };
}

function pickThrough(configure: (input: HTMLInputElement) => void): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    configure(input);
    input.onchange = () => {
      resolve([...(input.files ?? [])]);
      input.remove();
    };
    input.oncancel = () => {
      resolve([]);
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  });
}
